import Auth from '../../Utils/Auth/auth';
import {
  ACCOUNT_MANAGER,
  GENERAL_ADMIN,
  SALES_ROLE,
  STORE_ADMIN,
  TUTORIAL_KEYS,
} from '../../Utils/constants';
import { isEmptyObject } from '../../Utils/utils';
import AmplitudeEventTracker, {
  amplitudeParams,
} from './amplitude/AmplitudeEventTracker';

export const AnalyticsEventDictionary = {
  logInComplete: 'log_in_completed',
  passwordForgot: 'password_forgot',
  passwordRecovery: 'password_recovery_resent',
  profileDetails: 'profile_details_viewed',
  homePage: 'home_viewed',
  quickAccessClicked: 'quick_access_clicked',
  videoClicked: 'videos_clicked',
  orderCreated: 'new_order_created',
  codeQRCreadted: 'qr_code_created',
  paymentCodeCreated: 'payment_code_created',
  orderPaymentViewed: 'order_payment_viewed',
  transactionCancelled: 'merchant_transaction_cancelled',
  cancellationSelected: 'cancellation_selected',
  cancellationConfirmed: 'cancellation_confirmed',
  paymentRecordViewed: 'payment_record_viewed',
  paymentRecordExported: 'payment_record_exported',
  informationSearched: 'information_searched',
  addUser: 'add_users_selected',
  addOneUser: 'one_user_selected',
  addOneStore: 'one_store_selected',
  addStores: 'add_stores_selected',
  addMultipleUsers: 'multiple_user_selected',
  addMultipleStores: 'multiple_stores_selected',
  errorTrack: 'show_error',
  verifyStatus: 'verify_status',
  screenTime: 'screen_time_view',
  pageView: 'page_viewed',
  chargeQrViewLoaded: 'charge_qr_view_loaded',
  chargeQrGenerateOrderButtonClicked: 'charge_qr_generate_order_button_clicked',
  chargeQROrderCreatedViewLoaded: 'charge_qr_order_created_view_loaded',
  chargeQROrderCreatedViewCopyUrlButtonClicked:
    'charge_qr_order_created_view_copy_url_button_clicked',
  chargedQrOrderCreatedViewConfirmButtonClicked:
    'charged_qr_order_created_view_confirm_button_clicked',
  qrBasedOrderCreatedViewCancelButtonClicked:
    'qr_based_order_created_view_cancel_button_clicked',
  chargedQROrderSuccesViewLoaded: 'charged_qr_order_succes_view_loaded',
  chargedQROrderFailureViewLoaded: 'charged_qr_order_failure_view_loaded',
  paymentTutorialViewed: 'payment_tutorial_viewed',
  transactionTutorialViewed: 'transaction_tutorial_viewed',
  paymentReportViewed: 'payment_report_viewed',
  paymentReportFiltersSelected: 'payment_report_filters_selected',
  paymentReportDownloaded: 'payment_report_downloaded',
  bulkRefundButtonClicked: 'bulk_refund_button_clicked',
  bulkRefundReportLoaded: 'bulk_refund_report_loaded',
  bulkRefundReportError: 'bulk_refund_report_error',
  screenViewed: 'screen_viewed',
  merchantSize: 'merchant_size',
};

export const AnalyticsList = {
  logIn: 'log_in',
  merchantPayments: 'payment_report',
  account: 'me',
  faq: 'faq',
  help: 'help',
  bulkRefunds: 'bulk_refunds',
  stores: 'stores_management',
  user: 'users_management',
  payments: 'payments',
  sales: 'sales',
  paymentCode: 'payment_code',
  paymentCodeQR: 'payment_code_qr',
  paymentDetail: 'payment_detail',
  orderDetail: 'order_detail',
  home: 'home',
  refund: 'refund',
};

export const getUserProfile = role => {
  switch (role) {
    case GENERAL_ADMIN:
      return 'General';
    case STORE_ADMIN:
      return 'Admin';
    case ACCOUNT_MANAGER:
      return 'Manager';
    case SALES_ROLE:
      return 'Seller';
    default:
      return 'not_user_profile';
  }
};

const getStores = () => {
  try {
    const { stores } = Auth.userData();

    if (isEmptyObject(stores)) {
      return [];
    }

    return stores;
  } catch (error) {
    return { name: '', merchant_id: '' };
  }
};

export default function AnalyticsEvent() {
  return {
    pageView: function ({ page }) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.screenViewed,
        params: {
          [amplitudeParams.pageName]: page,
        },
      });
    },
    logInComplete: async function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.logInComplete,
        params: {
          [amplitudeParams.stores]: getStores().map(store => store.store_id),
        },
      });
    },
    passwordForgot: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.passwordForgot,
      });
    },
    passwordRecovery: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.passwordRecovery,
      });
    },
    profileDetails: function ({ branch }) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.profileDetails,
        params: {
          [amplitudeParams.branchInCharge]: branch,
        },
      });
    },
    homePage: async function () {
      await AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.homePage,
      });
    },
    quickAccessClicked: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.quickAccessClicked,
      });
    },
    videoClicked: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.videoClicked,
      });
    },
    orderCreated: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.orderCreated,
        params: {
          [amplitudeParams.orderType]: 'Monto',
        },
      });
    },
    paymentCodeCreated: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.paymentCodeCreated,
      });
    },
    orderPaymentViewed: function ({
      storeName,
      storeId,
      paymentId,
      paymentReference,
      totalPayment,
      totalItems,
      paymentLink,
    }) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.orderPaymentViewed,
        params: {
          [amplitudeParams.orderType]: 'Monto',
          [amplitudeParams.storeName]: storeName,
          [amplitudeParams.storeId]: storeId,
          [amplitudeParams.paymentId]: paymentId,
          [amplitudeParams.paymentReference]: paymentReference,
          [amplitudeParams.totalPayment]: totalPayment,
          [amplitudeParams.totalItems]: totalItems,
          [amplitudeParams.paymentLink]: paymentLink,
        },
      });
    },
    codeQRCreadted: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.codeQRCreadted,
      });
    },
    transactionCancelled: function ({
      storeName,
      storeId,
      paymentId,
      funnelStep,
    }) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.transactionCancelled,
        params: {
          [amplitudeParams.orderType]: 'Monto',
          [amplitudeParams.storeName]: storeName,
          [amplitudeParams.storeId]: storeId,
          [amplitudeParams.funnelStep]: funnelStep,
          [amplitudeParams.paymentId]: paymentId,
        },
      });
    },
    cancellationSelected: function ({
      storeName,
      storeId,
      funnelStep,
      paymentId,
      reimbursementType,
    }) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.cancellationSelected,
        params: {
          [amplitudeParams.reimbursementType]: reimbursementType,
          [amplitudeParams.storeName]: storeName,
          [amplitudeParams.orderType]: 'Monto',
          [amplitudeParams.storeId]: storeId,
          [amplitudeParams.paymentId]: paymentId,
          [amplitudeParams.funnelStep]: funnelStep,
        },
      });
    },
    cancellationConfirmed: function ({
      storeId,
      storeName,
      funnelStep,
      paymentId,
      reimbursementType,
    }) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.cancellationConfirmed,
        params: {
          [amplitudeParams.orderType]: 'Monto',
          [amplitudeParams.storeId]: storeId,
          [amplitudeParams.storeName]: storeName,
          [amplitudeParams.funnelStep]: funnelStep,
          [amplitudeParams.paymentId]: paymentId,
          [amplitudeParams.reimbursementType]: reimbursementType,
        },
      });
    },
    paymentRecordViewed: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.paymentRecordViewed,
      });
    },
    paymentRecordExported: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.paymentRecordExported,
      });
    },
    informationSearched: function ({ searchResults, funnelStep, inputType }) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.informationSearched,
        params: {
          [amplitudeParams.searchResults]: searchResults,
          [amplitudeParams.funnelStep]: funnelStep,
          [amplitudeParams.inputType]: inputType,
        },
      });
    },
    addUser: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.addUser,
      });
    },
    addOneUser: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.addOneUser,
      });
    },
    addMultipleUsers: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.addMultipleUsers,
      });
    },
    addStores: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.addStores,
      });
    },
    addOneStore: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.addOneStore,
      });
    },
    addMultipleStores: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.addMultipleStores,
      });
    },
    errorTrack: function ({ errorType, errorDescription }) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.errorTrack,
        params: {
          [amplitudeParams.errorType]: errorType,
          [amplitudeParams.errorDescription]: JSON.stringify(errorDescription),
        },
      });
    },
    inStore: function ({ eventId }) {
      AmplitudeEventTracker().trackEvent({
        name: eventId,
      });
    },
    tutorialViewed: function ({ moduleName = '' }) {
      let nameEvent = '';

      switch (moduleName) {
        case TUTORIAL_KEYS.merchantPaymentReport.name:
          nameEvent = AnalyticsEventDictionary.paymentTutorialViewed;
          break;
        case TUTORIAL_KEYS.merchatTransactionReport.name:
          nameEvent = AnalyticsEventDictionary.transactionTutorialViewed;
          break;
        default:
          console.log('No nameEvent selected');
      }

      AmplitudeEventTracker().trackEvent({
        name: nameEvent,
      });
    },
    paymentReportViewed: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.paymentReportViewed,
      });
    },
    paymentReportFiltersSelected: function ({ filterValue }) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.paymentReportFiltersSelected,
        params: {
          [amplitudeParams.filter]: filterValue,
        },
      });
    },
    downloadReport: function ({ eventName, filterValue }) {
      AmplitudeEventTracker().trackEvent({
        name: eventName,
        params: {
          [amplitudeParams.filter]: filterValue,
        },
      });
    },
    bulkRefundButtonClicked: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.bulkRefundButtonClicked,
      });
    },
    bulkRefundReportLoaded: function ({ action, error }) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.bulkRefundReportLoaded,
        params: {
          [amplitudeParams.bulkRefundsAction]: action,
          [amplitudeParams.bulkRefundsError]: error,
        },
      });
    },
    bulkRefundReportError: function () {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.bulkRefundReportError,
      });
    },
    screenViewed: function (
      pageName,
      employeeId,
      eventTime,
      logInDevice,
      logInLocation,
      merchantId,
      merchantName,
      merchantType,
      userProfile,
      storeId,
      storeName
    ) {
      AmplitudeEventTracker().trackEvent({
        name: AnalyticsEventDictionary.screenViewed,
        params: {
          [amplitudeParams.pageName]: pageName,
          [amplitudeParams.employeeId]: employeeId,
          [amplitudeParams.eventTime]: eventTime,
          [amplitudeParams.logInDevice]: logInDevice,
          [amplitudeParams.logInLocation]: logInLocation,
          [amplitudeParams.merchantId]: merchantId,
          [amplitudeParams.merchantName]: merchantName,
          [amplitudeParams.merchantType]: merchantType,
          [amplitudeParams.userProfile]: userProfile,
          [amplitudeParams.storeId]: storeId,
          [amplitudeParams.storeName]: storeName,
        },
      });
    },
  };
}
